import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./TopBar.css";
import ToolDraw from "../ToolDraw/ToolDraw";
import LayerMenu from "./LayerMenu";
import ToolModify from "../ToolModify/ToolModify";
import ToolMods from "../ToolMods/ToolMods";
import ToolScene from "../ToolScene/ToolScene";
import ToolText from "../ToolText/ToolText";
import ToolInstallation from "../ToolInstallation/ToolInstallation";
import { useDxfLoader } from "../../../hooks/useDxfLoader";
import ExportDxfDialog from "../../export/ExportDxfDialog";
import ExportPdfDialog from "../../export/ExportPdfDialog";
import FileMenu from "./FileMenu";
import OpenProjectDialog from "./OpenProjectDialog";
import { DXFWriter } from "../../../exports/dxf";
import { PdfExporter } from "../../../exports/pdf/PdfExporter";
import { useSelector, useDispatch } from "react-redux";
import ToolGrounding from "../ToolGrounding/ToolGrounding";
import ToolProject from "../ToolProject/ToolProject";
import ToolProjectType from "../ToolProjectType/ToolProjectType";
import UserGuidePopup from "./UserGuidePopup";
import { resetAllState } from "../../../redux/store";
import { store } from "../../../redux/store";
import { setProject, setProjectSaving, projectSaved, setProjectLoading, setShareAccess } from "../../../redux/projectSlice";
import { serializeProject } from "../../../modules/persistence";
import { createProject, saveProject } from "../../../services/projectApi";
import { incrementUpdateCounter } from "../../../redux/utilsSlice";
import { resetTabs, PROJECT_TABS } from "../../../redux/projectTabsSlice";
import { resetProject } from "../../../redux/projectSlice";
import { getIcon } from "../../../utils/icons";
import { PanelSettingsPopup } from "../../../modules/panel";
import ProjectSubBar from './ProjectSubBar';
import ToolNotes from '../ToolNotes/ToolNotes';
import ToolExport from '../ToolExport/ToolExport';
import ToolLayout from '../ToolLayout/ToolLayout';
import { selectActiveParty, selectEditorConfigLoaded } from '../../../redux/editorConfigSlice';
import UserCard from './UserCard';
import ProjectInfoCard from './ProjectInfoCard';
import SharePanel from './SharePanel';

const CadTopBar = ({ scenesRef, projectName = "", setProjectName, onFileSelect, scene
  ,defaultDxfFile // bu defaultDxfFile  parametresi dosyanın otomatik açılması için daha sonra kaldırılacak.
  ,attachmentDxfInfo // { url, token } — proje attachment'tan DXF yükleme
  ,onOpenProject // DrawingApp'ten gelen proje açma fonksiyonu
}) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isPdfExportOpen, setIsPdfExportOpen] = useState(false);
  const [isUserGuideOpen, setIsUserGuideOpen] = useState(false);
  const [isPanelSettingsOpen, setIsPanelSettingsOpen] = useState(false);
  const [isDxfLoading, setIsDxfLoading] = useState(false);
  const [loadingFileName, setLoadingFileName] = useState('');
  const [isOpenProjectOpen, setIsOpenProjectOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const { loadDxfFile } = useDxfLoader();
  const toolsCenterRef = useRef(null);
  const dispatch = useDispatch();
  const allLayers = useSelector((state) => state.layer.allLayers);
  const userRole = useSelector((state) => state.projectTabs.userRole);
  const activeTabId = useSelector((state) => state.projectTabs.activeTabId);
  const loadingMessage = useSelector((state) => state.dxf.loadingMessage);
  const projectState = useSelector((state) => state.project);
  const isShareMode = !!projectState.shareAccess;
  const isRestrictedRole = userRole === 'Yapı Denetim' || userRole === 'Dağıtım Kurumu' || userRole === 'Belediye';
  const isMimariTab = activeTabId === 'mimari';

  // Export dosya adı = aktif pafta adı (proje adı uzantısız + pafta suffix'i),
  // ProjectSubBar'daki sekme dosya adıyla aynı. Boşsa 'drawing'.
  const exportDefaultName = useMemo(() => {
    const base = projectName ? projectName.replace(/\.[^.]+$/, '') : '';
    const tab = PROJECT_TABS.find((t) => t.id === activeTabId);
    return (base + (tab?.suffix || '')) || 'drawing';
  }, [projectName, activeTabId]);

  // Backend-driven tool access
  const configLoaded = useSelector(selectEditorConfigLoaded);
  const activeParty = useSelector(selectActiveParty);

  // MVP/Beta: tüm araçlar tüm kullanıcılara açık — plan/rol filtresi devre dışı.
  // İleride plan bazlı kısıtlama gerektiğinde bu satır güncellenir.
  // eslint-disable-next-line no-unused-vars
  const accessibleTools = null; // null = no filtering
  const isToolVisible = useCallback((_toolId) => true, []);

  // Keyboard shortcuts for export
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "e") {
        e.preventDefault();
        setIsExportDialogOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const handleDxfChange = (e) => {
    const file = e.target.files[0];
    e.target.value = ""; // Aynı dosya tekrar seçilirse de çalışsın
    if (file) {
      // Reset scene and redux state before opening
      if (scene) {
        while (scene.children.length > 0) {
          const object = scene.children[0];
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(material => material.dispose());
            } else {
              object.material.dispose();
            }
          }
          scene.remove(object);
        }
      }
      dispatch(resetAllState());
      dispatch(resetTabs());     // Tüm sekmeleri kapat, sadece mimari kal
      dispatch(resetProject());  // Proje kimliğini sıfırla (yeni DXF = yeni proje)
      setLoadingFileName(file.name);
      setIsDxfLoading(true);
      // Dosya yüklemeyi bir sonraki tick'e bırak
      setTimeout(() => {
        setProjectName(file.name);
        loadDxfFile(file, () => setIsDxfLoading(false), () => setIsDxfLoading(false));
      }, 50);
    }
  };

  const handleImport = () => {
    document.getElementById("dxf-upload").click();
    setIsMenuOpen(false);
  };

  const handleExport = () => {
    setIsExportDialogOpen(true);
    setIsMenuOpen(false);
  };

  const handleConfirmExport = (filename) => {
    const writer = new DXFWriter();
    const dxfContent = writer.generateDXF(scene, { layer: { allLayers: allLayers || [] } });
    const blob = new Blob([dxfContent], { type: "application/dxf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.dxf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setIsExportDialogOpen(false);
  };

  const handleConfirmPdfExport = async (filename, colorMode) => {
    try {
      const exporter = new PdfExporter();
      const pdf = await exporter.generate(scene, colorMode);
      pdf.save(`${filename}.pdf`);
    } catch (err) {
      alert('PDF olusturulurken hata: ' + err.message);
    }
    setIsPdfExportOpen(false);
  };

  const handleResetProject = () => {
    if (window.confirm("Tüm proje verileri silinecek. Devam etmek istiyor musunuz?")) {
      // Clear the scene
      if (scene) {
        while (scene.children.length > 0) {
          const object = scene.children[0];
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(material => material.dispose());
            } else {
              object.material.dispose();
            }
          }
          scene.remove(object);
        }
      }

      // Reset Redux state
      dispatch(resetAllState());

      // Reset project name
      setProjectName("Yeni Proje");
    }
  };

  // Reset scene and redux state without prompting (used when opening examples)
  const resetSceneAndState = () => {
    // Clear the scene
    if (scene) {
      while (scene.children.length > 0) {
        const object = scene.children[0];
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
        scene.remove(object);
      }
    }

    // Reset Redux state
    dispatch(resetAllState());

    // Reset project name to default
    setProjectName("Yeni Proje");
  };

  const handleOpenExample = async (index) => {
    try {
      const filename = `ornek-${index}.dxf`;
      const url = `${process.env.PUBLIC_URL}/samples/${filename}`;
      setLoadingFileName(filename);
      setIsDxfLoading(true);
      const res = await fetch(url);
      if (!res.ok) throw new Error('Dosya yüklenemedi: ' + res.statusText);
      const blob = await res.blob();

      // Reset scene and state, then load the file
      resetSceneAndState();

      const file = new File([blob], filename, { type: 'application/dxf' });
      setProjectName(file.name);
      loadDxfFile(file, () => setIsDxfLoading(false), () => setIsDxfLoading(false));
    } catch (err) {
      console.error('Örnek açılırken hata:', err);
      alert('Örnek dosya açılamadı: ' + (err.message || err));
      setIsDxfLoading(false);
    }
  };

  // ─── Project Save ───
  //
  // Akış:
  // - Projesi olmayan (yeni çalışma) → backend'de yeni proje oluştur, kaydet
  // - Kendi projesi veya paydaş olduğu proje → doğrudan kaydet (backend role'ü doğrular)
  // - Paydaş değil + kayıt yetkisi yok → 403; kullanıcıya "Kopya oluştur?" teklif et (fork)
  const handleSave = useCallback(async () => {
    try {
      const token = localStorage.getItem('projabze_token');
      if (!token) { alert('Kaydetmek için giriş yapın.'); return; }

      dispatch(setProjectSaving(true));
      const state = store.getState();

      const blob = await serializeProject(state, { projectName, projectId: projectState.projectId }, scenesRef?.current);

      let pid = projectState.projectId;
      if (!pid) {
        const proj = await createProject(projectName || 'Yeni Proje');
        pid = proj.id;
        dispatch(setProject({ projectId: pid, projectName: proj.name, ownerId: proj.userId }));
      }

      let result;
      try {
        result = await saveProject(pid, blob, projectName || 'Proje');
      } catch (saveErr) {
        // 403: Paydaş değilsin, kayıt yetkin yok. Kullanıcıya kopya oluşturma teklifi.
        const is403 = /yetkiniz yok|403/i.test(saveErr.message || '');
        if (is403) {
          const confirmed = window.confirm(
            'Bu projede kayıt yetkiniz yok (paydaş olarak eklenmediğiniz bir proje).\n\nKendi hesabınıza bir kopya oluşturulsun mu?'
          );
          if (!confirmed) {
            dispatch(setProjectSaving(false));
            return;
          }
          // Fork: yeni proje oluştur, oraya kaydet
          const name = `${projectName || 'Proje'} (Kopya)`;
          const proj = await createProject(name);
          pid = proj.id;
          dispatch(setProject({ projectId: pid, projectName: proj.name, ownerId: proj.userId }));
          dispatch(setShareAccess(null));
          setProjectName(name);
          result = await saveProject(pid, blob, name);
          alert('Projeniz kopya olarak kendi hesabınıza kaydedildi.');
        } else {
          throw saveErr;
        }
      }
      dispatch(projectSaved({ version: result.version, savedAt: result.savedAt }));
      navigate(`/app/${pid}`, { replace: true });
    } catch (err) {
      console.error('[Save] Error:', err);
      alert('Kaydetme hatası: ' + (err.message || err));
      dispatch(setProjectSaving(false));
    }
  }, [dispatch, projectName, setProjectName, projectState.projectId, navigate]);

  // ─── Save As (always creates new project) ───
  const handleSaveAs = useCallback(async () => {
    const name = window.prompt('Proje adı:', projectName || 'Yeni Proje');
    if (!name) return;
    try {
      const token = localStorage.getItem('projabze_token');
      if (!token) { alert('Kaydetmek için giriş yapın.'); return; }

      dispatch(setProjectSaving(true));

      // Serialize CURRENT state (not the old project)
      const state = store.getState();
      const blob = await serializeProject(state, { projectName: name }, scenesRef?.current);

      const proj = await createProject(name);
      dispatch(setProject({ projectId: proj.id, projectName: name, ownerId: proj.userId }));
      setProjectName(name);

      const result = await saveProject(proj.id, blob, name);
      // Backend mirrors jbz into attachments/jbz automatically.

      dispatch(projectSaved({ version: result.version, savedAt: result.savedAt }));
      navigate(`/app/${proj.id}`, { replace: true });
    } catch (err) {
      console.error('[SaveAs] Error:', err);
      alert('Kaydetme hatası: ' + (err.message || err));
      dispatch(setProjectSaving(false));
    }
  }, [dispatch, projectName, setProjectName, navigate]);

  // ─── Open Project from server ───
  const handleOpenProject = useCallback(async (proj) => {
    setIsOpenProjectOpen(false);
    setIsMenuOpen(false);
    if (onOpenProject) {
      setLoadingFileName(proj.name);
      setIsDxfLoading(true);
      await onOpenProject(proj);
      setIsDxfLoading(false);
    }
  }, [onOpenProject]);

  // Ctrl+S shortcut
  useEffect(() => {
    const handleCtrlS = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    document.addEventListener('keydown', handleCtrlS);
    return () => document.removeEventListener('keydown', handleCtrlS);
  }, [handleSave]);

  useEffect(() => {
    if (defaultDxfFile) {
      const fileName = decodeURIComponent(defaultDxfFile.split('/').pop());
      setLoadingFileName(fileName);
      setIsDxfLoading(true);
      fetch(defaultDxfFile)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}: ${defaultDxfFile}`);
          return res.blob();
        })
        .then((blob) => {
          const file = new File([blob], fileName, { type: "application/dxf" });
          setProjectName(file.name);
          loadDxfFile(file, () => setIsDxfLoading(false), () => setIsDxfLoading(false));
        })
        .catch((err) => {
          console.error("Default DXF yüklenemedi:", err);
          setIsDxfLoading(false);
        });
    }
  }, [defaultDxfFile, loadDxfFile, setProjectName]);

  // ─── Load DXF from project attachment (when opening from Projects page) ───
  useEffect(() => {
    if (!attachmentDxfInfo) return;
    const { url, token } = attachmentDxfInfo;
    const fileName = decodeURIComponent(url.split('/').pop());
    setLoadingFileName(fileName);
    setIsDxfLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    // DrawingApp setProjectLoading(true) dispatch etti — DXF parse bitince false
    // dispatch ederek DrawingCanvas'taki fit-on-load useEffect'ini tetikle.
    const finishLoading = () => {
      setIsDxfLoading(false);
      dispatch(setProjectLoading(false));
    };
    fetch(url, { headers })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then(blob => {
        const ext = fileName.split('.').pop().toLowerCase();
        const file = new File([blob], fileName, { type: ext === 'jbz' ? 'application/octet-stream' : 'application/dxf' });
        loadDxfFile(file,
          () => {
            finishLoading();
          },
          (err) => {
            console.error('[ATTACH-DXF] loadDxfFile ERROR:', err);
            finishLoading();
          }
        );
      })
      .catch(err => {
        console.error('[ATTACH-DXF] fetch/parse error:', err);
        finishLoading();
      });
  }, [attachmentDxfInfo, loadDxfFile, dispatch]);

  return (

    <div className="topBar">
      <div className="tools">
        {/* Sol: Marka + File menüsü — sabit */}
        <div className="tools-left">
          <div className="topbar-editor-brand">
            <img src="/favicon.svg" alt="P" className="topbar-editor-brand-icon" />
            <span className="topbar-editor-brand-text">Pro<span>jabze</span><span className="beta-badge">beta</span></span>
          </div>
          <ul>
            <li onClick={toggleMenu}
              style={{ width: "100%", justifyContent: "center" }}
              className="file-menu-container">
              <a href="#">File</a>
              {isMenuOpen && (
                <FileMenu
                  onImport={isShareMode ? null : handleImport}
                  onExport={handleExport}
                  onExampleSelect={isShareMode ? null : handleOpenExample}
                  onOpen={isShareMode ? null : handleImport}
                  onSave={handleSave}
                  onSaveAs={handleSaveAs}
                  onOpenProject={isShareMode ? null : () => { setIsOpenProjectOpen(true); setIsMenuOpen(false); }}
                  onShare={isShareMode ? null : () => { setIsShareOpen(true); setIsMenuOpen(false); }}
                  isSaving={projectState.isSaving}
                  isShareMode={isShareMode}
                />
              )}
            </li>
          </ul>
        </div>

        {/* Orta: Araçlar — yatay scroll */}
        <div className="tools-center" ref={toolsCenterRef} onWheel={(e) => {
          if (toolsCenterRef.current) {
            e.preventDefault();
            toolsCenterRef.current.scrollLeft += e.deltaY;
          }
        }}>
          <ul>
            <li><LayerMenu /></li>
            {isToolVisible('tool-draw') && <li><ToolDraw /></li>}
            {isToolVisible('tool-modify') && <li><ToolModify /></li>}
            {isToolVisible('tool-text') && <li><ToolText /></li>}
            {isToolVisible('tool-grounding') && !isMimariTab && <li><ToolGrounding /></li>}
            {isToolVisible('tool-installation') && !isMimariTab && <li><ToolInstallation /></li>}
            {isToolVisible('tool-project') && isMimariTab && <li><ToolProject scene={scene} scenesRef={scenesRef} /></li>}
            {isToolVisible('tool-project-type') && <li><ToolProjectType /></li>}
            {!isMimariTab && <li><ToolLayout /></li>}
            {isToolVisible('tool-notes') && <li><ToolNotes /></li>}
            {isToolVisible('tool-mods') && <li><ToolMods /></li>}
            <li><ToolExport onExportDxf={() => setIsExportDialogOpen(true)} onExportPdf={() => setIsPdfExportOpen(true)} onShare={() => setIsShareOpen(true)} /></li>
            {!isMimariTab && (
            <li>
              <button className="reset-project-button topbar-icon-btn" onClick={() => dispatch(incrementUpdateCounter())} title="Dokümanları Güncelle">
                {getIcon("docUpdate", 16)}
                <span className="topbar-icon-btn-label">Güncelle</span>
              </button>
            </li>
            )}
            <li>
              <button className="reset-project-button topbar-icon-btn" onClick={() => setIsPanelSettingsOpen(true)} title="Panel Ayarları">
                <span style={{ fontSize: 16, lineHeight: 1 }}>⚙️</span>
                <span className="topbar-icon-btn-label">Ayarlar</span>
              </button>
            </li>
          </ul>
        </div>

        {/* Sağ: Proje bilgi kartı + Kullanıcı kartı — sabit */}
        <ProjectInfoCard />
        <UserCard />
      </div>
      {/* DXF Upload Input - Tek bir tane olmalı */}
      <input
        id="dxf-upload"
        type="file"
        accept=".dxf,.DXF"
        style={{ display: 'none' }}
        onChange={handleDxfChange}
      />
      <ExportDxfDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        onExport={handleConfirmExport}
        defaultFilename={exportDefaultName}
      />
      <ExportPdfDialog
        isOpen={isPdfExportOpen}
        onClose={() => setIsPdfExportOpen(false)}
        onExport={handleConfirmPdfExport}
        defaultFilename={exportDefaultName}
      />
      <UserGuidePopup
        isOpen={isUserGuideOpen}
        onClose={() => setIsUserGuideOpen(false)}
        pdfUrl={`${process.env.PUBLIC_URL}/kullanim-klavuzu-1.pdf`}
      />
      {isPanelSettingsOpen && (
        <PanelSettingsPopup onClose={() => setIsPanelSettingsOpen(false)} />
      )}
      <OpenProjectDialog
        isOpen={isOpenProjectOpen}
        onClose={() => setIsOpenProjectOpen(false)}
        onSelect={handleOpenProject}
      />
      <SharePanel
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
      />
      <ProjectSubBar projectName={projectName} />

      {(isDxfLoading || loadingMessage) && (
        <div className="dxf-loading-overlay">
          <div className="dxf-loading-content">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#7aa6ff" strokeWidth="2" strokeLinecap="round">
              <path d="M12 3v6l3 3" />
              <circle cx="12" cy="12" r="9" strokeDasharray="56" strokeDashoffset="14">
                <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
              </circle>
            </svg>
            <p>{loadingMessage || `${loadingFileName} yukleniyor...`}</p>
            <div className="dxf-loading-bar">
              <div className="dxf-loading-bar-fill"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CadTopBar;
